import { FC, useState } from 'react';

import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import { formatAddress } from 'helpers/address';
import * as Layout from 'layouts/StepperLayout';

import { CtaButton } from '../../../components/Buttons';
import commonLocales from '../../../components/commonLocales';
import {
  PhysicalOrderFlowSharedState,
  usePhysicalCardOrderFlowContext,
} from '../../hooks/usePhysicalCardOrderFlowContext';
import locales from './locales';
import * as S from './styles';

enum AddressOptions {
  Personal = 'PERSONAL',
  Professional = 'PROFESSIONAL',
}

function mapSharedStateToAddressOption({
  shouldSendToCompanyAddress,
}: PhysicalOrderFlowSharedState): AddressOptions | undefined {
  switch (shouldSendToCompanyAddress) {
    case true:
      return AddressOptions.Professional;
    case false:
      return AddressOptions.Personal;
    default:
      return undefined;
  }
}

const PhysicalCardOrderChooseDeliveryAddress: FC<StepProps> = (props) => {
  const { goBack, goNext } = props;

  const {
    flowConfig: {
      coordinates: { fullName, personalAddress, professionalAddress },
    },
    setSharedState,
    sharedState,
  } = usePhysicalCardOrderFlowContext();

  const [addressOption, setAddressOption] = useState<
    AddressOptions | undefined
  >(mapSharedStateToAddressOption(sharedState));

  const onNextPressed = () => {
    setSharedState({
      ...sharedState,
      shouldSendToCompanyAddress: addressOption === AddressOptions.Professional,
    });
    goNext({
      key:
        addressOption === AddressOptions.Personal
          ? 'verify-personal-address'
          : 'card-pin',
    });
  };

  const personalAddressFormatted = `${fullName}\n${formatAddress(
    personalAddress,
  )}`;

  const professionalAddressFormatted = `${fullName}\n${formatAddress(
    professionalAddress,
  )}`;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <S.Title>{locales.title}</S.Title>
          <S.SubText>{locales.subTitle}</S.SubText>

          <SunshineCard.RadioGroup
            onChange={(option) => setAddressOption(option as AddressOptions)}
            value={addressOption}
          >
            <SunshineCard.Group>
              <SunshineCard
                title={locales.labelPersonal}
                value={AddressOptions.Personal}
                variant="radio"
              >
                <SunshineCard.Content>
                  <SunshineCard.Text>
                    {personalAddressFormatted}
                  </SunshineCard.Text>
                </SunshineCard.Content>
              </SunshineCard>
              <SunshineCard
                title={locales.labelProfessional}
                value={AddressOptions.Professional}
                variant="radio"
              >
                <SunshineCard.Content>
                  <SunshineCard.Text>
                    {professionalAddressFormatted}
                  </SunshineCard.Text>
                </SunshineCard.Content>
              </SunshineCard>
            </SunshineCard.Group>
          </SunshineCard.RadioGroup>

          <Callout marginTop="space-32" variant="warning">
            <Callout.Title>{locales.warningBoxTitle}</Callout.Title>
            <Callout.Content>{locales.warningBoxText}</Callout.Content>
          </Callout>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <CtaButton isDisabled={!addressOption} onClick={onNextPressed}>
              {commonLocales.next}
            </CtaButton>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default PhysicalCardOrderChooseDeliveryAddress;
